import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import DropdownMenu from '../../dropdown-menu/DropdownMenu';
import IconButton from '../../button/IconButton';
import MediaDetailModal from './MediaDetailModal';
import MediaTranscriptionModal from './MediaTranscriptionModal';
import { VideoPreviewProps, MediaPreviewHandle, MediaMetadata, MenuItem, DeviceState } from '../../../types/media-preview';
import { getDetailLabelWithAcr } from '../../../utils/acr';
import { selectIsAudioEnabled } from '../../../app/actions/audio/audioSlice';
import { useHlsPlayer, isHlsSource } from '../../../hooks/useHlsPlayer';
import { downloadMediaFile } from '../../../utils/downloadFile';

// ===== fps 実測 =====

/** 実測 fps をスナップさせる標準フレームレート表 */
const STANDARD_FPS_TABLE = [23.976, 24, 25, 29.97, 30, 48, 50, 59.94, 60];

/** 実測 fps を最も近い標準値へスナップする（規格外の値は 5% 超のずれでそのまま返す） */
const snapToStandardFps = (fps: number): number => {
  let best = STANDARD_FPS_TABLE[0];
  for (const std of STANDARD_FPS_TABLE) {
    if (Math.abs(std - fps) < Math.abs(best - fps)) best = std;
  }
  return Math.abs(best - fps) / fps <= 0.05 ? best : fps;
};

/** requestVideoFrameCallback 対応環境用の最小型（TS DOM lib に無い環境向け） */
interface VideoFrameMetadataLike {
  mediaTime: number;
}
type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (
    _cb: (_now: number, _meta: VideoFrameMetadataLike) => void,
  ) => number;
  cancelVideoFrameCallback?: (_handle: number) => void;
};

/** fps 実測に使う連続フレーム間隔のサンプル数 */
const FPS_PROBE_SAMPLES = 10;

/**
 * VideoContainer: 動画要素のレイアウトコンテナ
 *
 * 重要: & > video ルールで height を明示的にモードごとに指定する。
 * StyledVideo 側のデフォルト height: auto と合わせ、CSS 優先度の競合を排除し
 * モバイル Safari の flexbox 実装でのフィードバックループ（動画が無限に拡大する現象）を防止する。
 *
 * HLS 動画はメタデータ読み込みまで intrinsic dimensions が 0x0 になるため、
 * aspect-ratio: auto 16/9 をフォールバックとして設定し、読み込み中のレイアウト崩壊を防ぐ。
 */
const VideoContainer = styled.div<{
  $mode: 'fixed-width' | 'fixed-height' | 'auto' | 'fill';
  $width?: string;
  $height?: string;
  $maxWidth?: string;
  $maxHeight?: string;
  $noRadius?: boolean;
}>`
  position: relative;
  ${props => props.$noRadius ? '' : `
    transform-style: preserve-3d;
    transform: perspective(1000px);
  `}
  transition: border-radius 0.5s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${props => {
    switch (props.$mode) {
      case 'fixed-width':
        return `
          width: ${props.$width || '100%'};
          height: auto;
          ${props.$noRadius ? 'border-radius: 0;' : ''}
          max-height: ${props.$maxHeight || '100dvh'};
          & > video {
            width: 100%;
            height: auto;
            max-height: ${props.$maxHeight || '100dvh'};
            aspect-ratio: auto 16 / 9;
          }
        `;
      case 'fixed-height':
        return `
          height: ${props.$height || 'auto'};
          width: auto;
          border-radius: ${props.$noRadius ? '0' : '6px'};
          ${props.$maxWidth ? `max-width: ${props.$maxWidth};` : ''}
          & > video { width: auto; height: 100%; }
        `;
      case 'fill':
        // 親（VideoPreviewRoot → PreviewWrapper → 利用側の definite height）に
        // 揃えて height:100% を通し、動画を object-fit で全面に敷く。
        return `
          width: 100%;
          height: 100%;
          max-height: 100%;
          & > video { width: 100%; height: 100%; }
        `;
      case 'auto':
      default:
        return `
          width: 100%;
          height: auto;
          ${props.$maxWidth ? `max-width: ${props.$maxWidth};` : ''}
          max-height: ${props.$maxHeight || '100dvh'};
          & > video {
            width: 100%;
            height: auto;
            max-height: ${props.$maxHeight || '100dvh'};
            aspect-ratio: auto 16 / 9;
          }
        `;
    }
  }}
`;

/**
 * StyledVideo: video 要素のベーススタイル
 *
 * height は必ず auto を使用すること。height: 100% を指定すると、
 * 親コンテナ（VideoContainer）の height: auto との間で循環参照が発生し、
 * モバイル Safari の flexbox レイアウトで動画が無限に拡大する。
 * 各 sizeMode で必要な height は VideoContainer の & > video ルールで上書きする。
 */
const StyledVideo = styled.video<{
  $objectFit?: 'cover' | 'contain';
  $noRadius?: boolean;
}>`
  width: 100%;
  height: auto;
  max-height: 100dvh;
  object-fit: ${props => props.$objectFit || 'contain'};
  border-radius: ${props => props.$noRadius ? '0' : '6px'};
  display: block;
`;

const ControlsOverlay = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  gap: 8px;
  z-index: 2;
`;

const OutsideControlsOverlay = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 4px 0 0;
`;

/** 外側コントロール行の左側スロット（MA オプション切替チップ等。右側の 3 点は維持） */
const OutsideLeftSlot = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const AcrBadge = styled.span`
  font-size: 18px;
  flex-shrink: 0;
  line-height: 1;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

const StatusBar = styled.div`
  font-size: 8px;
  color: var(--color-light-gray);
  padding: 4px 0;
`;

const remainingTimeBaseStyles = `
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  padding: 2px 6px;
  border-radius: 4px;
  line-height: 150%;
  z-index: 2;
  pointer-events: none;
`;

const RemainingTimeOverlay = styled.div.attrs({
  className: 'bodytext-11',
})`
  ${remainingTimeBaseStyles}
`;

/** 中央オーバーレイ共通スタイル（再生・巻き戻し・早送りアイコン表示用） */
const centerOverlayBaseStyles = css`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
  cursor: pointer;
  background: rgba(0, 0, 0, 0.15);

  .material-symbols-rounded {
    user-select: none;
    -webkit-user-select: none;
    -webkit-tap-highlight-color: transparent;
    filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.3));
  }
`;

/** モバイル: 再生/一時停止オーバーレイ */
const MobilePlayOverlay = styled.div`
  ${centerOverlayBaseStyles}
  z-index: 3;
`;

/** クリック一時停止時: 中央の再生・早送り・巻き戻しオーバーレイ（デスクトップ/モバイル共通） */
const PauseCenterOverlay = styled.div`
  ${centerOverlayBaseStyles}
  z-index: 4;
`;

/** カスタム再生バー: トラック（動画の外・直下に配置） */
const ProgressBarTrack = styled.div<{ $isMobile?: boolean }>`
  height: 3px;
  background: rgba(0, 0, 0, 0.08);
  cursor: pointer;
  overflow: hidden;
  margin: 0 ${p => (p.$isMobile ? '0' : '6px')};
  border-radius: ${p => (p.$isMobile ? '0' : '2px')};
`;

/** カスタム再生バー: 塗り */
const ProgressBarFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${p => p.$progress}%;
  background: var(--soremo-blue, #009ace);
  transition: width 0.15s linear;
`;

/** カスタム残り秒数表示（再生開始5秒間、フェードアウト付き） */
const PlayRemainingTimeOverlay = styled.div.attrs({
  className: 'bodytext-11',
})<{ $visible: boolean }>`
  ${remainingTimeBaseStyles}
  opacity: ${p => (p.$visible ? 1 : 0)};
  transition: opacity 1s ease-out;
`;

/** 最初から再生ボタン: コンテナ（OsCard準拠） */
const RestartContainer = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(255, 255, 255, 0.8);
  color: #000;
  border-radius: 4px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13px);
  font-size: 13px;
  line-height: 1;
  padding: 8px 12px;
  white-space: normal;
  outline: 0;
  transition: color 0.3s ease, transform 0.3s ease;
  z-index: 5;
  cursor: pointer;

  &:hover {
    color: #0076a5;
    transform: scale(1.05);
  }
`;

/** 最初から再生ボタン: アイコン部（OsCard準拠） */
const RestartButton = styled.div.attrs({
  className: 'material-symbols-rounded',
})`
  font-size: 16px;
  cursor: pointer;
  color: #009ace;
`;

/** VideoPreview ルートラッパー（flex アイテム幅制約）。fill 時は親の高さに追従する。 */
const VideoPreviewRoot = styled.div<{ $fill?: boolean }>`
  width: 100%;
  min-width: 0;
  ${props => (props.$fill ? 'height: 100%;' : '')}
`;

/** 中央オーバーレイ用アイコンスタイル */
const CENTER_OVERLAY_ICON_STYLE = { fontSize: '32px', color: 'var(--soremo-border)' } as const;
const CENTER_OVERLAY_PLAY_ICON_STYLE = { fontSize: '96px', color: 'var(--soremo-border)', opacity: 0.85 } as const;
const MOBILE_OVERLAY_PLAY_ICON_STYLE = { fontSize: '72px', color: 'var(--soremo-border)' } as const;

/** モバイル: 個別オーディオトグルボタン */
const MobileAudioToggleBtn = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 4;
  background: rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13px);
  -webkit-backdrop-filter: blur(13px);
  transition: all 0.3s ease;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;

  .material-symbols-rounded {
    font-size: 16px;
    color: var(--soremo-light-gray);
  }

  &:hover {
    transform: scale(1.1);
  }
`;

const VideoPreview = forwardRef<MediaPreviewHandle, VideoPreviewProps>(({
  src,
  thumbnailSrc = null,
  sizeMode = { mode: 'auto', objectFit: 'contain' },
  showMenu = false,
  showDownloadIcon = false,
  showStatusBar = false,
  autoPlay = false,
  hoverPlay = false,
  controls = false,
  loop = true,
  onLoaded = null,
  onPlayStateChange,
  fileName,
  fileSize,
  disableClickToPlay = false,
  showMobilePlayOverlay: showMobilePlayOverlayProp = false,
  showMobileAudioToggle: showMobileAudioToggleProp = false,
  menuOutside = false,
  onDownload,
  downloadUrl,
  fileId,
  acrScanStatus,
  acrScanResult,
  acrScannedAt,
  showProgressBar = false,
  showRemainingTimeOnPlay = false,
  showRestartOnPause = false,
  extraMenuItems,
  overrideDownload,
  downloadMenuLabel,
  outsideLeftContent,
}, ref) => {
  // --- State ---
  const [videoMetadata, setVideoMetadata] = useState<MediaMetadata | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wasPlayingBeforeHiddenRef = useRef(false);
  const [hasCalledOnLoaded, setHasCalledOnLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [remainingTime, setRemainingTime] = useState<string>('');
  const [videoMuted, setVideoMuted] = useState(true); // SP個別ミュート状態
  const [progress, setProgress] = useState(0); // 再生進捗 (0-100)
  const [playElapsed, setPlayElapsed] = useState(0); // 再生開始からの経過秒数
  const [pausedByClick, setPausedByClick] = useState(false); // クリック一時停止フラグ
  const [playRemainingTime, setPlayRemainingTime] = useState(''); // 再生時残り秒数

  // --- HLS再生対応 ---
  // .m3u8ファイルの場合はuseHlsPlayerで再生を管理（Safari: ネイティブHLS / その他: hls.js）
  const isHls = isHlsSource(src);
  const { hlsRef } = useHlsPlayer(src, videoRef, isHls);

  // --- fps 実測 ---
  // 実測済み fps（rVFC の mediaTime 差分から算出。src ごとにリセット）
  const probedFpsRef = useRef<number | null>(null);

  /** HLS マニフェストの FRAME-RATE 属性から fps を取得（未記載・非HLSは null） */
  const getHlsManifestFps = useCallback((): number | null => {
    const hls = hlsRef?.current;
    const levels = hls?.levels;
    if (!levels || levels.length === 0) return null;
    const idx = hls.currentLevel >= 0 ? hls.currentLevel : 0;
    const frameRate = levels[idx]?.frameRate;
    return frameRate && frameRate > 0 ? frameRate : null;
  }, [hlsRef]);

  /**
   * 現時点で判明している fps を返す（実測値 → HLS マニフェスト → null）。
   * BE の frame_rate が未解析なメディアのタイムコード換算補完に使う。
   */
  const resolveProbedFps = useCallback((): number | null => {
    if (probedFpsRef.current) return probedFpsRef.current;
    return getHlsManifestFps();
  }, [getHlsManifestFps]);

  // 再生中の連続フレームコールバック（rVFC）の mediaTime 差分から fps を実測する。
  // フレーム落ち・シークによる外れ値は中央値で除外し、標準フレームレートへスナップする。
  useEffect(() => {
    probedFpsRef.current = null;
    const video = videoRef.current as VideoWithFrameCallback | null;
    if (!src || !video?.requestVideoFrameCallback) return;

    let cancelled = false;
    let handle = 0;
    let prevMediaTime = -1;
    const deltas: number[] = [];

    const onFrame = (_now: number, meta: VideoFrameMetadataLike) => {
      if (cancelled || probedFpsRef.current) return;
      if (prevMediaTime >= 0) {
        const delta = meta.mediaTime - prevMediaTime;
        // 逆行（シーク）や 0.5 秒超の間隔（停止・大きなフレーム落ち）は捨てる
        if (delta > 0.001 && delta < 0.5) deltas.push(delta);
      }
      prevMediaTime = meta.mediaTime;
      if (deltas.length >= FPS_PROBE_SAMPLES) {
        const sorted = [...deltas].sort((a, b) => a - b);
        const median = sorted[Math.floor(sorted.length / 2)];
        if (median > 0) {
          const fps = snapToStandardFps(1 / median);
          probedFpsRef.current = fps;
          // ステータスバー・詳細モーダルの表示にも反映する
          setVideoMetadata(prev => (prev ? { ...prev, fps } : prev));
        }
        return;
      }
      handle = video.requestVideoFrameCallback!(onFrame);
    };

    handle = video.requestVideoFrameCallback(onFrame);
    return () => {
      cancelled = true;
      video.cancelVideoFrameCallback?.(handle);
    };
  }, [src]);

  // デバイス状態
  const deviceState = useSelector((state: { device: DeviceState }) => state.device);
  const { isMobile } = deviceState;

  // グローバルオーディオ状態を取得
  // effectiveMuted はグローバルトグルで一元制御（OFF→ミュート、ON→ミュート解除）
  // SP（showMobileAudioToggle有効時）は個別のvideoMutedを優先
  const isAudioEnabled = useSelector(selectIsAudioEnabled);
  const effectiveMuted = (isMobile && showMobileAudioToggleProp) ? videoMuted : !isAudioEnabled;

  // --- 外部API公開 ---
  useImperativeHandle(ref, () => ({
    play: async () => {
      if (videoRef.current) {
        await videoRef.current.play();
      }
    },
    pause: () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    },
    seek: (seconds: number) => {
      if (videoRef.current) {
        videoRef.current.currentTime += seconds;
      }
    },
    seekTo: (seconds: number) => {
      const video = videoRef.current;
      if (!video) return;
      const duration = Number.isFinite(video.duration) ? video.duration : Infinity;
      video.currentTime = Math.max(0, Math.min(duration, seconds));
    },
    getCurrentTime: () => {
      if (videoRef.current) {
        return videoRef.current.currentTime;
      }
      return 0;
    },
    getDuration: () => {
      if (videoRef.current) {
        return videoRef.current.duration;
      }
      return 0;
    },
    clickToggle: () => {
      if (!videoRef.current) return;
      if (videoRef.current.paused) {
        videoRef.current.play().catch(() => {});
        setPausedByClick(false);
      } else {
        videoRef.current.pause();
        setPausedByClick(true);
      }
    },
    getMediaElement: () => videoRef.current,
    getFps: () => resolveProbedFps(),
  }));

  useEffect(() => {
    if (videoRef.current && src) {
      const video = videoRef.current;

      const handleLoadedMetadata = () => {
        // fps はこの時点では確定しない（HLS マニフェスト値があればそれ、
        // なければ rVFC の実測完了時に setVideoMetadata で反映される）
        const fps = resolveProbedFps();
        setVideoMetadata({
          width: video.videoWidth,
          height: video.videoHeight,
          ...(fps != null ? { fps } : {}),
          format: isHls ? 'hls' : (src.split('?')[0].split('.').pop() || 'unknown')
        });

        // 読み込み完了を通知
        if (onLoaded && !hasCalledOnLoaded) {
          setHasCalledOnLoaded(true);
          onLoaded();
        }
      };

      const handlePlay = () => {
        setIsPlaying(true);
        onPlayStateChange?.(true);
      };

      const handlePause = () => {
        setIsPlaying(false);
        onPlayStateChange?.(false);
      };

      const handleEnded = () => {
        setIsPlaying(false);
        onPlayStateChange?.(false);
      };

      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('ended', handleEnded);
      
      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('ended', handleEnded);
      };
    }
  }, [src, isHls, onLoaded, hasCalledOnLoaded, onPlayStateChange, resolveProbedFps]);

  // アンマウント時・Activity 非表示時にビデオを停止し再生状態をリセット。
  // ref はクリーンアップ実行時に detach 済みの場合があるため effect 本体で要素を捕捉する。
  useEffect(() => {
    const video = videoRef.current;
    return () => {
      if (video && !video.paused) {
        video.pause();
        video.currentTime = 0;
      }
    };
  }, []);

  // 画面外に出たら一時停止、画面内に戻ったら再開（OSページ準拠）
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio < 0.1) {
            if (videoRef.current && !videoRef.current.paused) {
              wasPlayingBeforeHiddenRef.current = true;
              videoRef.current.pause();
            }
          } else if (wasPlayingBeforeHiddenRef.current && videoRef.current) {
            wasPlayingBeforeHiddenRef.current = false;
            videoRef.current.play().catch(() => {});
          }
        });
      },
      { threshold: [0, 0.1] },
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // グローバルオーディオ状態が変更されたときにビデオのmuted属性をリアルタイムで更新
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = effectiveMuted;
    }
  }, [effectiveMuted]);

  // 残り再生時間を計算するためのtimeupdateイベント
  const formatRemainingTime = useCallback((seconds: number): string => {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  useEffect(() => {
    if (!videoRef.current || !hoverPlay) return;
    const video = videoRef.current;

    const handleTimeUpdate = () => {
      if (video.duration && isFinite(video.duration)) {
        const remaining = video.duration - video.currentTime;
        setRemainingTime(formatRemainingTime(remaining));
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [hoverPlay, formatRemainingTime]);

  // カスタムコントロール用: 再生進捗・残り秒数・経過時間の追跡
  useEffect(() => {
    if (!videoRef.current || (!showProgressBar && !showRemainingTimeOnPlay)) return;
    const video = videoRef.current;

    const handleTimeUpdate = () => {
      if (!video.duration || !isFinite(video.duration)) return;

      if (showProgressBar) {
        setProgress((video.currentTime / video.duration) * 100);
      }

      if (showRemainingTimeOnPlay) {
        setPlayElapsed(video.currentTime);
        const remaining = video.duration - video.currentTime;
        setPlayRemainingTime(formatRemainingTime(remaining));
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [showProgressBar, showRemainingTimeOnPlay, formatRemainingTime]);

  // カスタムコントロール: 再生開始時に経過リセット
  useEffect(() => {
    if (!videoRef.current || !showRemainingTimeOnPlay) return;
    const video = videoRef.current;

    const handlePlay = () => {
      setPlayElapsed(video.currentTime);
    };

    video.addEventListener('play', handlePlay);
    return () => {
      video.removeEventListener('play', handlePlay);
    };
  }, [showRemainingTimeOnPlay]);

  const playVideo = useCallback(() => {
    videoRef.current?.play().catch((err: Error) => {
      if (err.name !== 'AbortError') {
        console.error('Error playing video:', err);
      }
    });
  }, []);

  const handleDownload = () => {
    // 呼び出し元による差し替え（MA スライドの一括 zip ダウンロード等）
    if (overrideDownload) {
      overrideDownload();
      return;
    }
    if (!downloadMediaFile({ downloadUrl, src, fileName, fallbackName: 'video' })) return;
    onDownload?.();
  };

  const handleDetailClick = () => {
    setShowDetailModal(true);
  };

  const hasAcrIcon = acrScanStatus === 'matched'
    || acrScanStatus === 'pending'
    || acrScanStatus === 'processing';
  const acrIconColor = acrScanStatus === 'matched' ? '#009ace' : 'var(--soremo-border)';

  const menuItems: MenuItem[] = (() => {
    const detailItem = getDetailLabelWithAcr(acrScanStatus);
    const items: MenuItem[] = [
      { label: detailItem.label, color: detailItem.color, onClick: handleDetailClick },
    ];
    items.push({ label: downloadMenuLabel ?? 'ダウンロード', onClick: handleDownload });
    // 文字起こし（fileId でバックエンドのメディア横断レジストリが解決できる場合のみ）
    if (fileId) {
      items.push({ label: '文字起こし', onClick: () => setShowTranscriptionModal(true) });
    }
    // 呼び出し元固有の追加メニュー（MA タイムチャート書き出し等）
    if (extraMenuItems?.length) items.push(...extraMenuItems);
    return items;
  })();

  const handleMouseEnter = () => {
    if (hoverPlay && videoRef.current) {
      setIsHovering(true);
      videoRef.current.play().catch(error => {
        // AbortError はホバー中に pause() が呼ばれた場合の想定内エラー（無視）
        if (error.name !== 'AbortError') {
          console.error('Error playing video on hover:', error);
        }
      });
    }
  };

  const handleMouseLeave = () => {
    if (hoverPlay && videoRef.current) {
      setIsHovering(false);
      wasPlayingBeforeHiddenRef.current = false;
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setRemainingTime('');
    }
  };

  const handleClick = () => {
    if (disableClickToPlay) return;
    if (!videoRef.current) return;

    if (showRestartOnPause) {
      if (isPlaying) {
        videoRef.current.pause();
        setPausedByClick(true);
        return;
      }
      if (pausedByClick) {
        playVideo();
        setPausedByClick(false);
        return;
      }
      playVideo();
      return;
    }

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      playVideo();
    }
  };

  // SP: 個別オーディオトグル
  const handleMobileAudioToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    const newMuted = !videoMuted;
    setVideoMuted(newMuted);
    videoRef.current.muted = newMuted;
  }, [videoMuted]);

  // SP: 再生ボタン押下
  const handleMobilePlay = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.play().catch(() => {});
  }, []);

  // SP: 巻き戻し（5秒）
  const handleMobileRewind = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5);
  }, []);

  // SP: 早送り（5秒）
  const handleMobileForward = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.min(
      videoRef.current.duration || 0,
      videoRef.current.currentTime + 5,
    );
  }, []);

  const handleResume = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    playVideo();
    setPausedByClick(false);
  }, [playVideo]);

  const handleRestart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.currentTime = 0;
    playVideo();
    setPausedByClick(false);
  }, [playVideo]);

  const handleProgressBarClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current || !videoRef.current.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    videoRef.current.currentTime = ratio * videoRef.current.duration;
    setProgress(ratio * 100);
  }, []);

  // SP + カスタムコントロール時は border-radius を無効化
  const noRadius = isMobile && showProgressBar;

  // 残り秒数の表示判定: 再生中は開始5秒以内に表示／一時停止時は常に右下に表示
  const isRemainingTimeVisible =
    (showRemainingTimeOnPlay && isPlaying && playElapsed < 4) ||
    (showRemainingTimeOnPlay && showRestartOnPause && pausedByClick && !isPlaying);
  const isRemainingTimeRendered =
    (showRemainingTimeOnPlay && isPlaying && playElapsed < 5) ||
    (showRemainingTimeOnPlay && showRestartOnPause && pausedByClick && !isPlaying);

  return (
    <VideoPreviewRoot ref={containerRef} $fill={sizeMode.mode === 'fill'}>
      <VideoContainer
        $mode={sizeMode.mode}
        $width={sizeMode.mode === 'fixed-width' ? sizeMode.width : undefined}
        $height={sizeMode.mode === 'fixed-height' ? sizeMode.height : undefined}
        $maxWidth={sizeMode.maxWidth}
        $maxHeight={sizeMode.maxHeight}
        $noRadius={noRadius}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      >
        <StyledVideo
          ref={videoRef}
          src={isHls ? undefined : src}
          poster={thumbnailSrc ?? undefined}
          autoPlay={autoPlay}
          muted={effectiveMuted}
          controls={controls}
          loop={loop}
          playsInline
          preload="metadata"
          $objectFit={sizeMode.objectFit}
          $noRadius={noRadius}
        />

        {/* ホバー再生時の残り再生時間表示 */}
        {hoverPlay && isHovering && remainingTime && (
          <RemainingTimeOverlay>{remainingTime}</RemainingTimeOverlay>
        )}

        {/* クリック一時停止時: 中央オーバーレイ（再生・早送り・巻き戻し） */}
        {showRestartOnPause && pausedByClick && !isPlaying && (
          <PauseCenterOverlay onClick={handleResume}>
            <span className="material-symbols-rounded" style={CENTER_OVERLAY_ICON_STYLE} onClick={handleMobileRewind}>
              replay_5
            </span>
            <span className="material-symbols-rounded" style={CENTER_OVERLAY_PLAY_ICON_STYLE} onClick={handleResume}>
              play_circle
            </span>
            <span className="material-symbols-rounded" style={CENTER_OVERLAY_ICON_STYLE} onClick={handleMobileForward}>
              forward_5
            </span>
          </PauseCenterOverlay>
        )}

        {/* SP: 再生/一時停止オーバーレイ（showRestartOnPause 時は PauseCenterOverlay を優先） */}
        {isMobile && showMobilePlayOverlayProp && !isPlaying && !(showRestartOnPause && pausedByClick) && (
          <MobilePlayOverlay>
            <span className="material-symbols-rounded" style={CENTER_OVERLAY_ICON_STYLE} onClick={handleMobileRewind}>
              replay_5
            </span>
            <span className="material-symbols-rounded" style={MOBILE_OVERLAY_PLAY_ICON_STYLE} onClick={handleMobilePlay}>
              play_circle
            </span>
            <span className="material-symbols-rounded" style={CENTER_OVERLAY_ICON_STYLE} onClick={handleMobileForward}>
              forward_5
            </span>
          </MobilePlayOverlay>
        )}

        {/* SP: 個別オーディオトグル */}
        {isMobile && showMobileAudioToggleProp && (
          <MobileAudioToggleBtn onClick={handleMobileAudioToggle}>
            <span className="material-symbols-rounded">
              {videoMuted ? 'volume_off' : 'volume_up'}
            </span>
          </MobileAudioToggleBtn>
        )}

        {/* 残り秒数: 再生中は開始5秒間、一時停止時は常に右下表示 */}
        {isRemainingTimeRendered && playRemainingTime && (
          <PlayRemainingTimeOverlay $visible={isRemainingTimeVisible}>
            {playRemainingTime}
          </PlayRemainingTimeOverlay>
        )}

        {/* 一時停止時: 最初から再生ボタン */}
        {showRestartOnPause && pausedByClick && !isPlaying && (
          <RestartContainer onClick={handleRestart}>
            <RestartButton>
              <div className="u-row u-gap4">
                replay
                <span className="bodytext-13 u-line-height-100">
                  最初から再生
                </span>
              </div>
            </RestartButton>
          </RestartContainer>
        )}

        {!menuOutside && (
          <ControlsOverlay onClick={(e) => e.stopPropagation()}>
            {hasAcrIcon && (
              <AcrBadge
                className="material-symbols-rounded"
                style={{ color: acrIconColor }}
                onClick={handleDetailClick}
              >
                error
              </AcrBadge>
            )}
            {showMenu && (
              <DropdownMenu items={menuItems} />
            )}
            {showDownloadIcon && !showMenu && (
              <IconButton onClick={handleDownload} icon="download" />
            )}
          </ControlsOverlay>
        )}
      </VideoContainer>

      {/* カスタムコントロール: 再生バー（動画の外・直下） */}
      {showProgressBar && (
        <ProgressBarTrack $isMobile={isMobile} onClick={handleProgressBarClick}>
          <ProgressBarFill $progress={progress} />
        </ProgressBarTrack>
      )}

      {menuOutside && (
        <OutsideControlsOverlay onClick={(e) => e.stopPropagation()}>
          {outsideLeftContent != null && (
            <OutsideLeftSlot>{outsideLeftContent}</OutsideLeftSlot>
          )}
          {hasAcrIcon && (
            <AcrBadge
              className="material-symbols-rounded"
              style={{ color: acrIconColor }}
              onClick={handleDetailClick}
            >
              error
            </AcrBadge>
          )}
          {showMenu && (
            <DropdownMenu items={menuItems} />
          )}
          {showDownloadIcon && !showMenu && (
            <IconButton onClick={handleDownload} icon="download" />
          )}
        </OutsideControlsOverlay>
      )}

      {showStatusBar && videoMetadata && (
        <StatusBar>
          {[
            `${videoMetadata.width} x ${videoMetadata.height}`,
            videoMetadata.fps != null ? `${Math.round(videoMetadata.fps * 100) / 100}fps` : null,
            videoMetadata.format,
          ].filter(Boolean).join(' • ')}
        </StatusBar>
      )}

      {showDetailModal && (
        <MediaDetailModal
          onClose={() => setShowDetailModal(false)}
          fileName={fileName}
          width={videoMetadata?.width}
          height={videoMetadata?.height}
          fps={videoMetadata?.fps}
          fileSize={fileSize}
          acrScanResult={acrScanResult}
          fileId={fileId}
          acrScanStatus={acrScanStatus}
          acrScannedAt={acrScannedAt}
        />
      )}

      {showTranscriptionModal && fileId && (
        <MediaTranscriptionModal
          fileId={fileId}
          fileName={fileName}
          onClose={() => setShowTranscriptionModal(false)}
          onSeek={(seconds) => {
            const video = videoRef.current;
            if (!video) return;
            const duration = Number.isFinite(video.duration) ? video.duration : Infinity;
            video.currentTime = Math.max(0, Math.min(duration, seconds));
          }}
        />
      )}
    </VideoPreviewRoot>
  );
});

VideoPreview.displayName = 'VideoPreview';

export default VideoPreview;
